import { type IListPageChildProps } from "layouts/ListPageLayout";
import { useState } from "react";
import { type RouteComponentProps } from "react-router";

import { useWebviewContext } from "@btnet/utilities-webview";

import { usePreviousLocation } from "utilities/flatRoutes/usePreviousLocation/usePreviousLocation";
import { lazyLoadComponent } from "utilities/LazyLoad.utility";
import { routes } from "utilities/routes";
import { readBooleanQueryParam } from "utilities/url/url";

import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import PageTitle from "commonComponents/utilities/PageTitle/PageTitle";

import "../../landingPage/LandingPage/LandingPage.less";
import "./JobFullPage.less";

const Job = lazyLoadComponent(() => import("entity/job/Job/Job"));

export interface IJobProps {
    jobId: string;
    defaultTab: string;
}

export function JobPage(_first: IListPageChildProps, routeProps: RouteComponentProps<IJobProps>) {
    return (
        <>
            <PageTitle title="Job Details" />
            <WrappedPage routeProps={routeProps} />
        </>
    );
}

interface IWrappedPageProps {
    routeProps: RouteComponentProps<IJobProps>;
}

function WrappedPage({ routeProps }: IWrappedPageProps) {
    const { location, history } = routeProps;
    const previous = usePreviousLocation();
    const { jobId, defaultTab } = routeProps.match.params;
    const updatedDefaultTab = defaultTab.split("?")[0];
    const search = location.search;
    const query = new URLSearchParams(search);
    const accessedFromContact = query.get("accessedFromContact") === "true";
    const accessedFromLead = query.get("accessedFromLead") === "true";
    const landOnSummaryAfterSave = query.get("landOnSummaryAfterSave") === "true";
    const ownerInvoiceId = query.get("ownerInvoiceId");
    const webviewContext = useWebviewContext();

    /**
     * when we refresh the page with a param, or navigate back to the page, react-router provides the param on location.search
     * when we "new job" -> "create from scratch", the query params are provided on location.pathname
     * this is defined in NewJobPrompt::74
     *
     * todo: when the JobRoute component is removed we should no longer need to check location.pathname
     */
    const openCondensed = readBooleanQueryParam(location, "openCondensed");

    // Latch per jobId: a tab switch drops the param but keeps the jobId (value survives); a save or
    // Save-and-new changes the jobId and re-reads, so the next job does not inherit this choice.
    const [latchedNewSelections, setLatchedNewSelections] = useState(() => ({
        jobId,
        value: readBooleanQueryParam(location, "createWithSelectionsReimagined"),
    }));
    if (latchedNewSelections.jobId !== jobId) {
        setLatchedNewSelections({
            jobId,
            value: readBooleanQueryParam(location, "createWithSelectionsReimagined"),
        });
    }
    const createWithSelectionsReimagined = latchedNewSelections.value;

    const redirectToJobLandingPage = () => {
        if (history.length > 1) {
            history.goBack();
        } else {
            history.push(routes.landingPage.path);
        }
    };

    const modalConfig: IModalConfiguration = {
        parentRoute: routes.landingPage.path,
        beforeClose: redirectToJobLandingPage,
    };

    const returnToListPage = () => {
        if (landOnSummaryAfterSave && !webviewContext) {
            history.replace(
                routes.jobPicker.selectJobLink(parseInt(jobId), routes.landingPage.getLink()),
            );
            return;
        }
        history.goBack();
    };

    return (
        <div className="JobFullPage">
            <Job
                id={parseInt(jobId)}
                defaultTab={updatedDefaultTab}
                accessedFromContact={accessedFromContact}
                accessedFromLead={accessedFromLead}
                landOnSummaryAfterSave={landOnSummaryAfterSave}
                createWithSelectionsReimagined={createWithSelectionsReimagined}
                ownerInvoiceId={parseInt(ownerInvoiceId ?? "")}
                openCondensed={openCondensed}
                modalConfig={modalConfig}
                onBack={returnToListPage}
                previousLocation={previous}
                webviewContext={webviewContext}
                {...routeProps}
            />
        </div>
    );
}
